var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css } from 'lit';
import { property } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map.js';
/**
 * A circular loading indicator component that shows activity or ongoing processes.
 *
 * @example
 * ```html
 * <kr-spinner></kr-spinner>
 * <kr-spinner size="lg"></kr-spinner>
 * <kr-spinner color="light"></kr-spinner>
 * <kr-spinner color="#ff5500"></kr-spinner>
 * ```
 */
export class KRSpinner extends LitElement {
    constructor() {
        super(...arguments);
        /**
         * The size of the spinner. Can be 'sm', 'md', 'lg', 'xl', or any valid CSS size value (e.g., '100px', '2rem').
         */
        this.size = 'md';
        /**
         * The color of the spinner. Can be 'dark', 'light', or any valid CSS color value.
         */
        this.color = 'dark';
    }
    render() {
        var normalizedSize = '';
        var normalizedColor = '';
        if (this.size == 'sm') {
            normalizedSize = '16px';
        }
        else if (this.size == 'md') {
            normalizedSize = '24px';
        }
        else if (this.size == 'lg') {
            normalizedSize = '32px';
        }
        else if (this.size == 'xl') {
            normalizedSize = '48px';
        }
        else {
            normalizedSize = this.size;
        }
        if (this.color == 'dark') {
            normalizedColor = '#163052';
        }
        else if (this.color == 'light') {
            normalizedColor = '#ffffff';
        }
        else {
            normalizedColor = this.color;
        }
        return html `
      <svg
        class="spinner"
        style=${styleMap({
            width: normalizedSize,
            height: normalizedSize,
            color: normalizedColor
        })}
        viewBox="0 0 44 44"
        role="status"
        aria-label="Loading"
      >
        <circle
          class="circle"
          cx="22"
          cy="22"
          r="20"
          fill="none"
          stroke-width="4"
        />
      </svg>
    `;
    }
}
KRSpinner.styles = css `
    :host {
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }

    .spinner {
      display: block;
      animation: spin 1.4s linear infinite;
    }

    .spinner .circle {
      animation: dash 1.4s ease-in-out infinite;
      stroke-dasharray: 80, 200;
      stroke-dashoffset: 0;
    }

    @keyframes spin {
      to {
        transform: rotate(360deg);
      }
    }

    @keyframes dash {
      0% {
        stroke-dasharray: 1, 200;
        stroke-dashoffset: 0;
      }
      50% {
        stroke-dasharray: 100, 200;
        stroke-dashoffset: -15;
      }
      100% {
        stroke-dasharray: 100, 200;
        stroke-dashoffset: -125;
      }
    }

    .circle {
      stroke: currentColor;
      stroke-linecap: round;
    }
  `;
__decorate([
    property({
        type: String,
        reflect: true,
    })
], KRSpinner.prototype, "size", void 0);
__decorate([
    property({
        type: String,
        reflect: true,
    })
], KRSpinner.prototype, "color", void 0);
// Guarded registration: no-op if the tag is already defined, so a second copy of krubble on
// the page (e.g. the Operations shell loads krubble globally while an embedded app bundled its
// own copy) doesn't throw. First definition wins.
if (!customElements.get('kr-spinner')) {
    customElements.define('kr-spinner', KRSpinner);
}
//# sourceMappingURL=spinner.js.map